import { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, addDoc, query, where, getDocs, orderBy, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { useAuth } from './AuthContext';

const GoalsContext = createContext();

export function GoalsProvider({ children }) {
  const { currentUser } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch user's goals
  useEffect(() => {
    if (!currentUser) {
      setGoals([]);
      setLoading(false);
      return;
    }

    const fetchGoals = async () => {
      try {
        const goalsRef = collection(db, 'goals');
        const q = query(
          goalsRef, 
          where('userId', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        const goalsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setGoals(goalsData);
      } catch (error) {
        console.error('Error fetching goals:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGoals();
  }, [currentUser]);

  // Add new goal
  const addGoal = async (goalData) => {
    try {
      const goalsRef = collection(db, 'goals');
      const newGoal = {
        ...goalData,
        userId: currentUser.uid,
        status: 'active',
        progress: 0,
        createdAt: new Date(),
        lastModified: new Date(),
        isPrivate: goalData.isPrivate || false,
      };
      
      await addDoc(goalsRef, newGoal);
    } catch (error) {
      console.error('Error adding goal:', error);
      throw error;
    }
  };

  const updateGoalProgress = async (goalId, progress) => {
    try {
      const goalRef = doc(db, 'goals', goalId);
      await updateDoc(goalRef, { 
        progress,
        lastModified: new Date()
      });
      
      setGoals(prev => prev.map(goal => 
        goal.id === goalId ? { ...goal, progress } : goal
      ));
    } catch (error) {
      console.error('Error updating goal progress:', error);
      throw error;
    }
  };

  const deleteGoal = async (goalId) => {
    try {
      const goalRef = doc(db, 'goals', goalId);
      await deleteDoc(goalRef);
      
      setGoals(prev => prev.filter(goal => goal.id !== goalId));
    } catch (error) {
      console.error('Error deleting goal:', error);
      throw error;
    }
  };

  const updateGoal = async (goalId, updates) => {
    try {
      const goalRef = doc(db, 'goals', goalId);
      await updateDoc(goalRef, {
        ...updates,
        lastModified: new Date()
      });
    } catch (error) {
      console.error('Error updating goal:', error);
      throw error;
    }
  };

  return (
    <GoalsContext.Provider value={{ 
      goals, 
      loading, 
      addGoal,
      updateGoalProgress,
      deleteGoal,
      updateGoal 
    }}>
      {children}
    </GoalsContext.Provider>
  );
}

export function useGoals() {
  return useContext(GoalsContext);
} 